/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, ChangeEvent } from 'react';
import dayjs from 'dayjs';
import {
  Sizes,
  Positions,
  Icons,
  TextInput,
  Card,
} from '@sede-enterprise/shell-ds-react-framework';
import { useSelector } from 'react-redux';
import { ActionMenu, NGContainer } from './style';
import Table from 'src/UI/Table';
import Button from 'src/UI/Button';
import { searchByText } from 'src/utils/common/utils';
import { NUMBER_20 } from 'src/utils/table';
import { get } from 'src/services/GenesisApi';
import { DOWNLOAD_MARKETS, GET_MARKETS, MESSAGES } from 'src/constants/EUMarkets';
import DownloadButton from 'src/UI/Download';
import { COMMON_MESSAGES } from 'src/constants/Common';
import { sendNotification } from 'src/UI/Notification';
import { IMarketItem } from 'src/types/EUMarkets.type';
import { IFullState } from 'src/types/All.type';
import { rolePermissions } from 'src/constants/Permission';
import { getEnv } from 'src/utils/env.vite';
import AddEditMarket from './AddEdit';
import ViewBalances from './ViewBalances';

interface IMarkets {
  id: number;
  marketName: string;
  marketType: string;
  shortName: string;
}

const { ArrowCcw, Search, Add: AddIcon, EllipsisVertical } = Icons;

// Add/Modify are only available in the Dev environment until the save APIs are ready elsewhere.
const isDevEnvironment = () => getEnv('VITE_APP_ENVIRONMENT') === 'Dev';

const Markets: React.FC = () => {
  const [markets, setMarkets] = useState<IMarketItem[]>([]);
  const [market, setMarket] = useState<IMarketItem | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [openBalances, setOpenBalances] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const { permissions } = useSelector((state: IFullState) => state.genesisUserRolePermission);

  const getData = () => {
    // API call to fetch data
    setLoading(true);
    get(GET_MARKETS)
      .then((response) => {
        setMarkets(response.data);
      })
      .catch(() => {
        sendNotification({
          type: 'error',
          message: MESSAGES.SERVER_ERROR,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getData();
  }, []);
  const handleModalOpenClose = () => {
    if (open) {
      setMarket(null);
    }
    setOpen(!open);
  };

  const handleEdit = (item: IMarketItem) => {
    setMarket(item);
    setOpen(true);
  };

  const handleBalancesModalOpenClose = () => {
    if (openBalances) {
      setMarket(null);
    }
    setOpenBalances(!openBalances);
  };

  const handleShowBalances = (item: IMarketItem) => {
    setMarket(item);
    setOpenBalances(true);
  };

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const renderData = (text: string, data: IMarketItem[]) => {
    return searchByText(data, text, ['marketName', 'marketType', 'shortName']);
  };

  const renderActions = (item: IMarketItem) => (
    <ActionMenu
      action='click'
      popup={
        <div className='menu-container'>
          {isDevEnvironment() && permissions.includes(rolePermissions.updateMasterData) && (
            <Button
              data-testid={`btn-modify-${item.id}`}
              size='small'
              onClick={() => handleEdit(item)}
              variant='transparent'
            >
              Modify
            </Button>
          )}
        </div>
      }
    >
      <Button
        size='small'
        data-testid={`btn-actions-${item.id}`}
        variant='transparent'
        onClick={() => {}}
        icon={<EllipsisVertical />}
        iconOnly
      />
    </ActionMenu>
  );

  let columns = [
    {
      accessKey: 'marketName',
      fieldName: 'marketName',
      title: 'Market Name',
      sorting: true,
    },
    {
      accessKey: 'shortName',
      fieldName: 'shortName',
      title: 'Short Name',
      sorting: true,
    },
    {
      accessKey: 'marketType',
      fieldName: 'marketType',
      title: 'Market Type',
      sorting: true,
    },
    {
      accessKey: 'validFrom',
      fieldName: 'validFrom',
      title: 'Valid From',
      sorting: true,
      render: (item: IMarketItem) =>
        item.validFrom ? dayjs(item.validFrom).format(COMMON_MESSAGES.DATE_DD_MMM_YYYY) : '-',
    },
    {
      accessKey: 'validTo',
      fieldName: 'validTo',
      title: 'Valid To',
      sorting: true,
      render: (item: IMarketItem) =>
        item.validTo ? dayjs(item.validTo).format(COMMON_MESSAGES.DATE_DD_MMM_YYYY) : 'No Expiry',
    },
    {
      accessKey: 'balances',
      fieldName: 'balances',
      title: 'View Balances',
      render: (item: IMarketItem) => (
        <Button
          data-testid={`btn-balances-${item.id}`}
          size='small'
          variant='transparent'
          onClick={() => handleShowBalances(item)}
          key={`tsk-balances-btn-${item.id}`}
          className='logs-info'
        >
          View
        </Button>
      ),
    },
    permissions.includes(rolePermissions.updateMasterData) &&
      isDevEnvironment() && {
        accessKey: 'actions',
        fieldName: 'actions',
        title: 'Actions',
        render: (item: IMarketItem) => renderActions(item),
      },
  ];
  columns = columns.filter((_) => _ !== false);

  return (
    <NGContainer>
      <div className='action-container'>
        <div className='search-container'>
          <TextInput
            placeholder='Search'
            onChange={handleSearch}
            suffix={{
              node: <Search height={24} width={24} />,
            }}
            size={Sizes.Small}
          />
        </div>
        {isDevEnvironment() && permissions.includes(rolePermissions.createMasterData) && (
          <Button
            onClick={handleModalOpenClose}
            data-testid='add-new-market'
            icon={<AddIcon />}
            size={Sizes.Small}
            variant='outlined'
            iconPosition={Positions.Right}
          >
            Add
          </Button>
        )}
        <DownloadButton
          downloadURL={DOWNLOAD_MARKETS}
          method='GET'
          fileName='Markets'
          fileType='xlsx'
          buttonProp={{
            size: Sizes.Small,
            variant: 'outlined',
            className: 'master-data-download master-data-download-secondary',
            iconOnly: true,
            'aria-label': 'Download Markets as Excel',
            title: 'Download Markets as Excel',
          }}
        />
        <Button
          size='small'
          iconOnly
          icon={<ArrowCcw />}
          onClick={getData}
          disabled={loading}
          data-testid='btn-refresh'
        />
      </div>
      <Card className='table-card market-table'>
        <Table
          data={renderData(searchText, markets)}
          columns={columns}
          totalRecords={renderData(searchText, markets).length}
          recordsPerPage={NUMBER_20}
          isLoading={loading}
        />
      </Card>
      <AddEditMarket
        open={open}
        onClose={handleModalOpenClose}
        market={market}
        refreshData={getData}
      />
      {openBalances && (
        <ViewBalances
          open={openBalances}
          onClose={handleBalancesModalOpenClose}
          balances={market?.balances ?? []}
          marketName={market?.marketName ?? ''}
        />
      )}
    </NGContainer>
  );
};

export default Markets;
